import type {
  Maybe,
  PremiumHubTestimonials,
  PremiumHubTestimonialsTestimonialsCollection,
} from 'bundles/premium-hub/__generated__/baseContentfulTypes';
import type { CareerRolesModulesFieldsFragment } from 'bundles/premium-hub/__generated__/contentfulTypes';
import { ProductVariant, UnifiedHubProductVariant } from 'bundles/premium-hub/constants';
import type { DegreeCategoryFields_Dedupe1Fragment } from 'bundles/premium-hub/queries/__generated__/DegreeCategoryFieldsFragment';
import type { PremiumHubGetDegreesById_Dedupe1Query as GetDegreesByIdQuery } from 'bundles/premium-hub/queries/__generated__/GetDegreesById';
import type { DegreesByMarketingType_Dedupe1_FieldsFragment as DegreesByMarketingTypeFieldsFragment } from 'bundles/premium-hub/queries/__generated__/GetDegreesByMarketingType';
import type { PremiumProductFields_Dedupe1Fragment } from 'bundles/premium-hub/queries/__generated__/PremiumProductFieldsFragment';
import type { GetCareerRolesByIdQuery } from 'bundles/premium-hub/queries/__generated__/getCareerRolesById';
import type {
  CareerRolesItem,
  CareerRolesModuleData,
  Degree,
  PPCDegreeCategory,
  Partner,
  PremiumProductProps,
  PremiumProductWithMetadata,
  ProductCardProps,
} from 'bundles/premium-hub/types';
import { filterNullOrUndefined } from 'bundles/premium-hub/utils';

export const convertPremiumProductToDegree = (product: PremiumProductFields_Dedupe1Fragment): Degree => {
  const domainIds = product.domainIds.filter(filterNullOrUndefined);
  return {
    ...product,
    domainIds,
  };
};

const normalizeProductVariant = (productVariant: string) => {
  switch (productVariant) {
    case 'MASTERS_DEGREE':
    case 'MastersDegree':
      return ProductVariant.MastersDegree;
    case 'BACHELORS_DEGREE':
    case 'BachelorsDegree':
      return ProductVariant.BachelorsDegree;
    case 'POSTGRADUATE_DIPLOMA':
    case 'PostgraduateDiploma':
      return ProductVariant.PostgraduateDiploma;
    default:
      return productVariant;
  }
};

export const convertPpcPremiumProductToPremiumProductProps = (
  product: PremiumProductFields_Dedupe1Fragment
): PremiumProductProps => {
  const domainIds = product.domainIds.filter(filterNullOrUndefined);
  const partnerIds = product.partnerEntities.map((partner) => partner.id);
  const partners: Partner[] = product.partnerEntities.map((partner) => {
    const { name, id, squareLogo, location } = partner;
    return {
      name,
      id,
      squareLogo,
      location: location ? { country: location.country ?? undefined } : undefined,
    };
  });

  const normalizedProductVariant = normalizeProductVariant(product.productVariant);

  return {
    ...product,
    productVariant: normalizedProductVariant,
    marketingName: product.name,
    bannerImage: product.thumbnailUrl,
    partnerIds,
    domainIds,
    partners: { elements: partners },
    partner: partners[0],
    degreeAttributes: {
      hasScholarship: product?.degreeAttributes?.hasScholarship,
      tuitionInUsd: product?.degreeAttributes?.tuitionInUsd,
    },
  };
};

export const convertDegreeCategoryFieldsToPPCDegreeCategory = (
  degreeCategory: DegreeCategoryFields_Dedupe1Fragment
): PPCDegreeCategory => {
  const { degreeCount } = degreeCategory;
  return {
    ...degreeCategory,
    degreeCount: degreeCount ?? 0,
  };
};

export const convertPremiumProductWithMetadataToPremiumHubProductCardProps = (
  product: PremiumProductWithMetadata
): ProductCardProps => {
  const {
    productVariant,
    id,
    slug,
    name,
    bannerImage,
    upcomingTermDates,
    partners,
    atAGlance,
    partnerContentful,
    programDeadlines,
    degreeAttributes,
  } = product;
  const firstPartner = partners?.elements?.[0];
  const convertedPartner = firstPartner
    ? {
        id: firstPartner.id,
        name: firstPartner.name,
        squareLogo: firstPartner.squareLogo ?? '',
        partnerMonotoneLogo: partnerContentful?.partnerMonotoneLogo?.url ?? '',
        partnerLogo: partnerContentful?.partnerLogo?.url ?? '',
        country: firstPartner?.location?.country ?? '',
      }
    : undefined;
  return {
    id,
    slug,
    name,
    bannerImage: bannerImage ?? undefined,
    degreeLevel: productVariant ?? undefined,
    duration: atAGlance?.timeFrame ?? undefined,
    upcomingTermDates: upcomingTermDates ?? undefined,
    productVariant: UnifiedHubProductVariant.Degree,
    partner: convertedPartner,
    description: atAGlance?.ranking ?? undefined,
    timeFrame: atAGlance?.timeFrame ?? undefined,
    programDeadlines,
    degreeAttributes: {
      hasScholarship: degreeAttributes?.hasScholarship,
      tuitionInUsd: degreeAttributes?.tuitionInUsd,
    },
  };
};

export const convertTestimonialsForProps = (
  testimonialsSection?: Maybe<PremiumHubTestimonials>
): PremiumHubTestimonialsTestimonialsCollection => {
  const collection: PremiumHubTestimonialsTestimonialsCollection = {
    total: -1,
    skip: -1,
    limit: -1,
    items: [],
  };

  if (testimonialsSection?.testimonialsCollection?.items) {
    const items = testimonialsSection.testimonialsCollection.items.filter(filterNullOrUndefined);
    collection.items = items;
    collection.total = items.length;
  }

  return collection;
};

export const convertDegreeByMarketingTypeToPPCFields = (
  degree: DegreesByMarketingTypeFieldsFragment
): PremiumProductFields_Dedupe1Fragment => {
  return {
    ...degree,
    shortName: degree.shortName ?? '',
    programDeadlines: [],
    __typename: 'PremiumProductCollections_learningProduct',
  };
};

export const getEventingProductTypeFromVariant = (productVariant?: string | null) => {
  if (!productVariant) return undefined;

  switch (productVariant) {
    case UnifiedHubProductVariant.Degree:
      return 'degree';
    case UnifiedHubProductVariant.Mastertrack:
      return 'master_track';
    case UnifiedHubProductVariant.UniversityCertificate:
      return 'premium_certificate';
    case UnifiedHubProductVariant.ProfessionalCertificate:
      return 's12n';
    default:
      return undefined;
  }
};

export const removeProductIdPrefix = (id: string) => id.replace(/^degree~/, '');

export const normalizeCareerRolesModuleData = (
  contentfulCareerRolesModule?: CareerRolesModulesFieldsFragment | null,
  careerRoles?: GetCareerRolesByIdQuery,
  degrees?: GetDegreesByIdQuery
): CareerRolesModuleData | null => {
  if (!careerRoles && !degrees) {
    return null;
  }
  // career role mapped by role id
  const careerRolesById: Record<string, CareerRolesItem> = {};
  careerRoles?.PathwaySkillCareerRoles?.queryRolesByIds?.forEach((role) => {
    careerRolesById[role.id] = role;
  });

  // related degree mapped by degree id
  const degreesById: Record<string, PremiumProductFields_Dedupe1Fragment> = {};
  degrees?.PremiumProductCollections?.multiGetDegrees?.forEach((degree) => {
    if (degree) {
      degreesById[degree.id.replace('degree', 'base')] = degree;
    }
  });

  return {
    title: contentfulCareerRolesModule?.title ?? '',
    roleItems: contentfulCareerRolesModule?.rolesCollection?.items?.map((item) => {
      if (!item) return null;

      return {
        roleId: item.roleId,
        roleName: careerRolesById[item?.roleId ?? '']?.name,
        roleDescription: item?.description,
        jobPostingData: careerRolesById[item?.roleId ?? '']?.jobPostingData,
        parentRoleId: item.parentRoleId,
        parentRoleName: careerRolesById[item?.parentRoleId ?? '']?.name,
        parentRoleJobPostingData: careerRolesById[item?.parentRoleId ?? '']?.jobPostingData,
        testimonialImage: item?.image,
        testimonialHeading: item?.testimonialHeading,
        testimonialBody: item?.testimonialBody,
        relatedDegree: degreesById[item?.relatedDegreeId ?? ''],
      };
    }),
  };
};
